<template>
    <div class="scan-details-page flex flex-col h-screen bg-gray-100 overflow-hidden">
        <!-- Top Navigation Bar -->
        <div class="scan-details-topbar flex-shrink-0 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-3 md:px-6 z-40">
            <div class="flex items-center gap-3">
                <button type="button" @click="goBack" class="btn btn-secondary topbar-btn flex items-center gap-2">
                    <LineIcon name="arrow-left" :size="15" :stroke-width="1.8" />
                    <span class="hidden sm:inline">{{ $t('scanDetails.backToScans') }}</span>
                </button>
                <div v-if="scan?.is_checked" class="flex items-center gap-2 rounded-lg shadow-sm checked-badge-status" style="height: 42px; padding: 0 1.25rem; background: linear-gradient(135deg, #10b981 0%, #059669 100%); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);">
                    <div class="scan-details-checkbox-container relative">
                        <input type="checkbox" checked disabled class="scan-details-checkbox" style="width: 1.125rem; height: 1.125rem; background: transparent; border: none;" />
                        <LineIcon name="check" :size="15" :stroke-width="1.8" class="scan-details-checkbox-icon" style="font-size: 0.7rem; color: white;" />
                    </div>
                    <span class="text-sm uppercase hidden sm:inline" style="font-weight: 600; color: white;">{{ $t('invoices.checked') }}</span>
                </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="flex items-center gap-2">
                <button type="button" @click="navigateToNext" :disabled="!nextInvoiceId" class="btn btn-secondary topbar-btn flex items-center gap-2" :title="$t('scanDetails.nextInvoice')">
                    <LineIcon name="chevron-left" :size="15" :stroke-width="1.8" />
                    <span class="hidden sm:inline">{{ $t('common.next') }}</span>
                </button>

                <button type="button" @click="navigateToPrevious" :disabled="!previousInvoiceId" class="btn btn-secondary topbar-btn flex items-center gap-2" :title="$t('scanDetails.previousInvoice')">
                    <span class="hidden sm:inline">{{ $t('common.previous') }}</span>
                    <LineIcon name="chevron-right" :size="15" :stroke-width="1.8" />
                </button>

            </div>
            <div class="flex items-center gap-2">
                <button type="button" @click="showExportModal = true" class="btn btn-secondary topbar-btn flex items-center gap-2">
                    <LineIcon name="download" :size="15" :stroke-width="1.8" />
                    <span class="hidden sm:inline">{{ $t('scanDetails.export') }}</span>
                </button>


                <!-- Settings Dropdown -->
                <div class="relative">
                    <button type="button" @click.stop="toggleSettingsDropdown" class="btn btn-secondary topbar-btn flex items-center gap-2">
                        <LineIcon name="settings" :size="15" :stroke-width="1.8" />
                        <span class="hidden sm:inline">{{ $t('scanDetails.settings') }}</span>
                    </button>
                    <!-- The app's own menu: hairline panel, quiet rows, the
                         same marks as the table's row actions. -->
                    <div v-if="showSettingsDropdown" class="topbar-menu" @click.stop>
                        <button type="button" @click="handleRescanClick" class="topbar-menu-item">
                            <LineIcon name="rotate-right" :size="14" :stroke-width="1.8" />
                            <span>{{ $t('scanDetails.rescanInvoice') }}</span>
                        </button>
                        <button type="button" @click="handleDelete" class="topbar-menu-item topbar-menu-item--danger">
                            <LineIcon name="trash" :size="14" :stroke-width="1.8" />
                            <span>{{ $t('scanDetails.deleteInvoice') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="loading" class="m-6 bg-white rounded-lg p-16 text-center shadow-sm">
            <div class="w-12 h-12 border-4 border-gray-200 border-t-emerald-500 rounded-full animate-spin mx-auto mb-5"></div>
            <p class="text-gray-600">{{ $t('scanDetails.loadingInvoice') }}</p>
        </div>

        <div v-else-if="error" class="m-6 bg-white rounded-lg p-16 text-center shadow-sm">
            <p class="text-red-600 mb-4">{{ error }}</p>
            <button type="button" @click="loadScan" class="btn btn-primary">{{ $t('scanDetails.tryAgain') }}</button>
        </div>

        <div v-else-if="scan" class="flex flex-1 overflow-hidden flex-col md:flex-row">
            <!-- Left Pane: Document Preview -->
            <div class="flex-none h-[300px] md:flex-1 md:h-auto bg-gray-700 flex flex-col overflow-hidden border-r border-gray-200 relative">
                <div class="h-12 bg-gray-800/90 flex items-center justify-center gap-3 px-4 border-b border-gray-600 z-10 flex-shrink-0">
                    <div class="flex items-center gap-2">
                        <button type="button" @click="zoomOut" class="p-1.5 text-gray-200 hover:bg-white/10 rounded transition-colors" :title="$t('scanDetails.zoomOut')">
                            <LineIcon name="zoom-out" :size="15" :stroke-width="1.8" />
                        </button>
                        <span class="text-gray-200 text-xs font-medium min-w-[40px] text-center">{{ Math.round(scale * 100) }}%</span>
                        <button type="button" @click="zoomIn" class="p-1.5 text-gray-200 hover:bg-white/10 rounded transition-colors" :title="$t('scanDetails.zoomIn')">
                            <LineIcon name="zoom-in" :size="15" :stroke-width="1.8" />
                        </button>
                    </div>
                    <div class="w-px h-6 bg-gray-600 mx-2"></div>
                    <div class="flex items-center gap-2">
                        <button type="button" @click="rotateLeft" class="p-1.5 text-gray-200 hover:bg-white/10 rounded transition-colors" :title="$t('scanDetails.rotateLeft')">
                            <LineIcon name="rotate-left" :size="15" :stroke-width="1.8" />
                        </button>
                        <button type="button" @click="rotateRight" class="p-1.5 text-gray-200 hover:bg-white/10 rounded transition-colors" :title="$t('scanDetails.rotateRight')">
                            <LineIcon name="rotate-right" :size="15" :stroke-width="1.8" />
                        </button>
                    </div>
                    <div class="w-px h-6 bg-gray-600 mx-2"></div>
                    <button type="button" @click="resetView" class="p-1.5 text-gray-200 hover:bg-white/10 rounded transition-colors" :title="$t('scanDetails.resetView')">
                        <LineIcon name="crosshair" :size="15" :stroke-width="1.8" />
                    </button>
                </div>

                <div class="flex-1 w-full h-full overflow-hidden flex items-center justify-center relative bg-[radial-gradient(#4b5563_1px,transparent_1px)] [background-size:20px_20px]" ref="previewContainer" @wheel.prevent="handleWheel">
                    <div class="max-w-[900px] w-full h-full flex items-center justify-center">
                        <div v-if="thumbnailLoaded"
                             class="max-w-full max-h-full transition-transform duration-100 ease-out origin-center flex items-center justify-center"
                             @mousedown="startDrag"
                             @mousemove="onDrag"
                             @mouseup="stopDrag"
                             @mouseleave="stopDrag"
                             @touchstart.prevent="startDrag"
                             @touchmove.prevent="onDrag"
                             @touchend="stopDrag"
                             ref="imageElement"
                        >
                            <img :src="rotatedImageUrl || originalImageUrl"
                                 :alt="scan?.result?.invoice_number || $t('scanDetails.invoice')"
                                 class="max-w-full max-h-full object-contain shadow-lg select-none pointer-events-none"
                                 @error="thumbnailLoaded = false"
                                 draggable="false"

                            />
                        </div>
                        <div v-else class="flex flex-col items-center justify-center text-gray-400 p-10 border-2 border-dashed border-gray-500 rounded-lg bg-gray-800/50">
                            <LineIcon name="keyboard" :size="22" :stroke-width="1.8" class="mb-4 opacity-50" />
                            <span class="text-lg font-medium mb-2">Manual Entry</span>
                            <span class="text-sm">No document preview available</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Pane: Data Form -->
            <div class="details-pane w-full md:w-[500px] lg:w-[600px] xl:w-[800px] 2xl:w-[900px] bg-white flex flex-col overflow-hidden flex-1 md:flex-none md:h-full">
                <!-- Header & Tabs -->
                <div class="flex-shrink-0 border-b border-gray-200 bg-white z-10">
                    <div class="p-4 pb-0">
                        <div class="flex justify-between items-center mb-4 scan-details-header">
                            <h2 class="text-lg font-semibold text-gray-900">{{ $t('scanDetails.title') }}</h2>
                            <span class="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 uppercase">{{ scan.status }}</span>
                        </div>

                        <div class="flex justify-center bg-gray-100 rounded-lg self-start" style="padding: 0.25rem;">
                            <button
                                v-for="tab in tabs"
                                :key="tab.id"
                                @click="activeTab = tab.id"
                                class="text-sm font-medium rounded-md transition-all"
                                :class="activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                                style="padding: 0.5rem 1.5rem;"
                            >
                                {{ tab.label }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Total Mismatch Warning Banner -->
                <div v-if="totalMismatch && editForm.line_items && Array.isArray(editForm.line_items) && editForm.line_items.length > 0" class="flex-shrink-0 bg-orange-50 border-l-4 border-orange-500 mx-6 my-2 rounded-r">
                    <div class="pl-10 pr-6 py-4">
                        <h3 class="text-base font-bold text-orange-900 mb-2">{{ $t('scanDetails.totalMismatchWarning') || 'Total Mismatch Detected' }}</h3>
                        <p class="text-sm text-orange-800 mb-1.5">
                            <strong>{{ $t('scanDetails.invoiceNumber') || 'Invoice' }} {{ editForm.invoice_number }}:</strong>
                            {{ $t('scanDetails.calculated') || 'Calculated' }}: <strong>{{ lineItemsCalculatedTotal.toFixed(2) }}</strong>,
                            {{ $t('scanDetails.grandTotal') || 'Grand Total' }}: <strong>{{ (parseFloat(editForm.grand_total) || 0).toFixed(2) }}</strong>,
                            {{ $t('scanDetails.difference') || 'Difference' }}: <strong class="text-red-600">{{ Math.abs(totalDifference).toFixed(2) }}</strong>
                        </p>
                        <p class="text-xs text-orange-700 mt-1">
                            {{ $t('scanDetails.reviewLineItems') || 'Please review line items and totals to ensure accuracy.' }}
                        </p>
                    </div>
                </div>

                <!-- Subtotal Mismatch Warning Banner -->
                <div v-if="subtotalMismatch && editForm.line_items && Array.isArray(editForm.line_items) && editForm.line_items.length > 0" class="flex-shrink-0 bg-orange-50 border-l-4 border-orange-500 mx-6 my-2 rounded-r" style="padding: 1rem 1.5rem 1rem 2.5rem;">
                    <div>
                        <h3 class="text-base font-bold text-orange-900 mb-2">Subtotal Mismatch Detected</h3>
                        <p class="text-sm text-orange-800 mb-1.5">
                            <strong>{{ editForm.invoice_number }}:</strong>
                            Line Items Total: <strong>{{ subtotalDifference.itemsTotal.toFixed(2) }}</strong>,
                            Subtotal: <strong>{{ subtotalDifference.subtotal.toFixed(2) }}</strong>,
                            Difference: <strong class="text-red-600">{{ Math.abs(subtotalDifference.difference).toFixed(2) }}</strong>
                        </p>
                        <p class="text-xs text-orange-700 mt-1">
                            The sum of line item totals does not match the invoice subtotal. Please review.
                        </p>
                        <!-- Only offered when the gap actually looks like VAT. When it is
                             some other amount a single line was misread, and deducting VAT
                             would overwrite the lines that are correct. -->
                        <button v-if="subtotalMismatchIsVatShaped" @click="deductVatFromLineItems" type="button" class="btn btn-sm mt-3" style="background-color: #ea580c; border-color: #ea580c; color: white;">
                            <LineIcon name="calculator" :size="15" :stroke-width="1.8" class="mr-1" /> Deduct VAT from line item totals
                        </button>
                    </div>
                </div>

                <!-- Tab Content -->
                <div class="flex-1 overflow-y-auto bg-gray-50 min-h-0">

                    <!-- INVOICE TAB (Consolidated View) -->
                    <div v-if="activeTab === 'invoice'" class="p-6">
                        <div class="bg-white shadow-sm border border-gray-200 rounded-lg p-6 space-y-4">

                            <!-- Invoice Details FIRST -->
                            <div class="form-section">
                                <h3 class="form-section-title">{{ $t('scanDetails.invoiceDetails') }}</h3>
                                <div class="grid grid-cols-2 gap-3">
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.documentType') }} <span class="required">*</span></label>
                                        <SelectField v-model="editForm.invoice_type" :options="invoicetypeOptions" class="form-input" />
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.invoiceNumber') }} <span class="required">*</span></label>
                                        <input v-model="editForm.invoice_number" type="text" :class="getFieldClass('invoice_number')" class="form-input-bold font-mono" maxlength="10" @input.stop="editForm.invoice_number = (editForm.invoice_number || '').replace(/\s+/g, '')" />
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.issueDate') }} <span class="required">*</span></label>
                                        <input v-model="editForm.invoice_date" type="text" class="form-input" placeholder="DD/MM/YYYY" @input.stop />
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.dueDate') }} <span class="required">*</span></label>
                                        <input v-model="editForm.due_date" type="text" class="form-input" placeholder="DD/MM/YYYY" @input.stop />
                                    </div>
                                </div>

                                <!-- Type Radio Buttons -->
                                <div class="form-group mt-4">
                                    <label class="form-label">{{ $t('scanDetails.type') }} <span class="required">*</span></label>
                                    <div class="flex gap-6 mt-2">
                                        <label class="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                v-model="editForm.type"
                                                value="expense"
                                                class="w-4 h-4 text-primary focus:ring-primary focus:ring-2"
                                            />
                                            <span class="text-sm font-medium text-gray-700">{{ $t('scanDetails.expense') }}</span>
                                        </label>
                                        <label class="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                v-model="editForm.type"
                                                value="income"
                                                class="w-4 h-4 text-primary focus:ring-primary focus:ring-2"
                                            />
                                            <span class="text-sm font-medium text-gray-700">{{ $t('scanDetails.income') }}</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <!-- Seller Section -->
                            <div class="form-section">
                                <div class="flex items-center justify-between mb-3">
                                    <h3 class="form-section-title mb-0">{{ $t('scanDetails.fromSeller') }}</h3>
                                    <button
                                        type="button"
                                        @click="openSellerModal"
                                        class="select-seller-btn ml-1"
                                        title="Select Vendor"
                                        @mouseenter="$event.currentTarget.style.backgroundColor='#357abd'"
                                        @mouseleave="$event.currentTarget.style.backgroundColor='#4a90e2'"
                                        style="background-color: #4a90e2; color: white; padding: 4px 8px; height: 32px; border: none; border-radius: 4px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background-color 0.2s;">
                                        <LineIcon name="users" :size="15" :stroke-width="1.8" class="text-sm" style="color: white;" />
                                    </button>
                                </div>
                                <div>
                                    <div class="grid" :class="authStore.useVendorCode && editForm.type !== 'income' ? 'grid-cols-2 gap-3' : 'grid-cols-1'">
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.sellerName') }}</label>
                                            <input v-model="editForm.seller_name" type="text" :class="getFieldClass('seller_name')" class="form-input-bold" :placeholder="$t('scanDetails.sellerName')" @input.stop />
                                        </div>
                                        <!-- Vendor Code for Seller (when we are the buyer) -->
                                        <div v-if="authStore.useVendorCode && editForm.type !== 'income'" class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.vendorCode') }}</label>
                                            <input v-model="editForm.vendor_code" type="text" class="form-input" :placeholder="$t('scanDetails.vendorCodePlaceholder')" @input.stop />
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.address') }}</label>
                                        <input v-model="editForm.seller_address" type="text" class="form-input" :placeholder="$t('scanDetails.address')" @input.stop />
                                    </div>
                                    <div class="grid grid-cols-2 gap-3">
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.vatNumber') }}</label>
                                            <input v-model="editForm.seller_vat_number" type="text" :class="[getVatInputClass(sellerVatValidation), missingFields.has('seller_vat_number') ? '!border-red-500 bg-red-50' : '']" :placeholder="$t('scanDetails.vatNumber')" @input.stop />
                                        </div>
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.taxId') }}</label>
                                            <input v-model="editForm.seller_tax_id" type="text" class="form-input" :placeholder="$t('scanDetails.taxId')" @input.stop />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Buyer Section -->
                            <div class="form-section">
                                <div class="flex items-center justify-between mb-3">
                                    <h3 class="form-section-title mb-0">{{ $t('scanDetails.billToBuyer') }}</h3>
                                    <button
                                        type="button"
                                        @click="openBuyerModal"
                                        class="select-seller-btn ml-1"
                                        title="Select Vendor"
                                        @mouseenter="$event.currentTarget.style.backgroundColor='#357abd'"
                                        @mouseleave="$event.currentTarget.style.backgroundColor='#4a90e2'"
                                        style="background-color: #4a90e2; color: white; padding: 4px 8px; height: 32px; border: none; border-radius: 4px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background-color 0.2s;">
                                        <LineIcon name="users" :size="15" :stroke-width="1.8" class="text-sm" style="color: white;" />
                                    </button>
                                </div>
                                <div>
                                    <div class="grid" :class="authStore.useVendorCode && editForm.type === 'income' ? 'grid-cols-2 gap-3' : 'grid-cols-1'">
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.buyerName') }}</label>
                                            <input v-model="editForm.buyer_name" type="text" :class="getFieldClass('buyer_name')" class="form-input-bold" :placeholder="$t('scanDetails.buyerName')" @input.stop />
                                        </div>
                                        <!-- Vendor Code for Buyer (when we are the seller) -->
                                        <div v-if="authStore.useVendorCode && editForm.type === 'income'" class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.vendorCode') }}</label>
                                            <input v-model="editForm.vendor_code" type="text" class="form-input" :placeholder="$t('scanDetails.vendorCodePlaceholder')" @input.stop />
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.address') }}</label>
                                        <input v-model="editForm.buyer_address" type="text" class="form-input" :placeholder="$t('scanDetails.address')" @input.stop />
                                    </div>
                                    <div class="grid grid-cols-2 gap-3">
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.vatNumber') }}</label>
                                            <input v-model="editForm.buyer_vat_number" type="text" :class="[getVatInputClass(buyerVatValidation), missingFields.has('buyer_vat_number') ? '!border-red-500 bg-red-50' : '']" :placeholder="$t('scanDetails.vatNumber')" @input.stop />
                                        </div>
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.taxId') }}</label>
                                            <input v-model="editForm.buyer_tax_id" type="text" class="form-input" :placeholder="$t('scanDetails.taxId')" @input.stop />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Totals Section -->
                            <div class="form-section">
                                <div class="flex items-center justify-between mb-3">
                                    <h3 class="form-section-title mb-0">{{ $t('scanDetails.totals') }}</h3>
                                    <div class="flex items-center gap-2">
                                        <label class="form-label mb-0 text-xs">{{ $t('scanDetails.currency') }}:</label>
                                        <input v-model="editForm.currency" type="text" :class="getFieldClass('currency')" style="width: 80px; padding: 0.375rem 0.5rem; text-align: center;" placeholder="EUR" maxlength="10" @input.stop />
                                    </div>
                                </div>
                                <div class="grid grid-cols-3 gap-3">
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.baseAmount') }}</label>
                                        <input v-model="editForm.base_amount" type="text" class="form-input" placeholder="0.00" @input.stop />
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.subtotal') }}</label>
                                        <input v-model="editForm.subtotal" type="text" class="form-input" placeholder="0.00" @input.stop />
                                    </div>
                                    <div class="form-group">
                                        <label class="form-label">{{ $t('scanDetails.vatAmount') }} <span class="required">*</span></label>
                                        <input v-model="editForm.tax_amount" type="text" :class="getFieldClass('tax_amount')" @input.stop />
                                    </div>
                                    <div class="form-group col-span-3">
                                        <label class="form-label">{{ $t('scanDetails.taxExemptionReason', 'Tax Exemption Reason') }}</label>
                                        <input v-model="editForm.tax_exemption_reason" type="text" class="form-input" @input.stop />
                                    </div>
                                    <div class="form-group col-span-3">
                                        <label class="form-label">{{ $t('scanDetails.total') }} <span class="required">*</span></label>
                                        <input v-model="editForm.grand_total" type="text" :class="[getFieldClass('grand_total'), totalMismatch ? 'border-orange-500 bg-orange-50' : '']" class="form-input-large" @input.stop />
                                        <div v-if="totalMismatch && editForm.line_items && Array.isArray(editForm.line_items) && editForm.line_items.length > 0" class="text-xs text-orange-600 mt-1">
                                            ⚠️ Line items total: {{ lineItemsCalculatedTotal.toFixed(2) }} (Diff: {{ totalDifference > 0 ? '+' : '' }}{{ totalDifference.toFixed(2) }})
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Line Items Table -->
                            <div class="mt-6">
                                <div class="line-items-header flex justify-between items-center mb-2">
                                    <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ $t('scanDetails.lineItems') }}</h3>
                                    <button type="button" @click="addLineItem" class="btn btn-primary btn-sm">
                                        <LineIcon name="plus" :size="15" :stroke-width="1.8" class="mr-1" />
                                        {{ $t('scanDetails.addItem') }}
                                    </button>
                                </div>

                                <div class="line-items-wrap border border-gray-200 rounded-lg overflow-x-auto">
                                    <table class="text-sm text-left" style="min-width: 100%;">
                                        <thead class="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                        <tr>
                                            <th v-if="hasBarcodes" class="px-3 py-2 w-[108px]">{{ $t('scanDetails.barcode') }}</th>
                                            <th class="px-3 py-2 w-[60px]">{{ $t('scanDetails.qty') }}</th>
                                            <th class="px-3 py-2 w-[64px]">{{ $t('scanDetails.unit') }}</th>
                                            <th class="px-3 py-2 min-w-[220px]">{{ $t('scanDetails.description') }}</th>
                                            <th class="px-3 py-2 w-[88px] text-right">{{ $t('scanDetails.price') }}<template v-if="displayCurrency"> ({{ displayCurrency }})</template></th>
                                            <th v-if="hasNetPrice" class="px-3 py-2 w-[88px] text-right">{{ $t('scanDetails.netPrice', 'Net Price') }}</th>
                                            <th v-if="hasNetQuantity" class="px-3 py-2 w-[80px] text-right">{{ $t('scanDetails.pricePerUnit', 'Price/Unit') }}</th>
                                            <th v-if="hasNetQuantity" class="px-3 py-2 w-[72px] text-right">{{ $t('scanDetails.netQty', 'Net Qty') }}</th>
                                            <th v-if="hasNetAmount" class="px-3 py-2 w-[88px] text-right">{{ $t('scanDetails.netAmount', 'Net Amt') }}</th>
                                            <th v-if="hasDiscounts" class="px-3 py-2 w-[72px] text-right">{{ $t('scanDetails.discount') }}</th>
                                            <th v-if="hasDiscounts" class="px-3 py-2 w-[56px] text-right">{{ $t('scanDetails.discountPct', 'Disc%') }}</th>
                                            <th v-if="hasTaxInfo" class="px-3 py-2 w-[64px] text-right">{{ $t('scanDetails.taxRate', 'Tax Rate') }}</th>
                                            <th v-if="hasTaxInfo" class="px-3 py-2 w-[88px] text-right">{{ $t('scanDetails.taxAmount', 'Tax Amt') }}</th>
                                            <th v-if="hasExciseDuty" class="px-3 py-2 w-[88px] text-right">{{ $t('scanDetails.exciseDuty', 'Excise Duty') }}</th>
                                            <th class="px-3 py-2 w-[96px] text-right">{{ $t('scanDetails.total') }}<template v-if="displayCurrency"> ({{ displayCurrency }})</template></th>
                                            <th v-if="hasTotalWithTax" class="px-3 py-2 w-[104px] text-right">{{ $t('scanDetails.totalWithTax', 'Total w/ Tax') }}<template v-if="displayCurrency"> ({{ displayCurrency }})</template></th>
                                            <th v-if="hasCurrencies" class="px-3 py-2 w-[64px] text-right">{{ $t('scanDetails.currency') }}</th>
                                            <th v-if="hasBatchNumbers" class="px-3 py-2 w-[96px] text-right">{{ $t('scanDetails.batchNumber') }}</th>
                                            <th v-if="hasExpiryDates" class="px-3 py-2 w-[96px] text-right">{{ $t('scanDetails.expiryDate') }}</th>
                                            <th class="px-3 py-2 w-8"></th>
                                        </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-100">
                                        <template v-if="editForm.line_items && Array.isArray(editForm.line_items) && editForm.line_items.length">
                                            <tr v-for="(item, index) in editForm.line_items" :key="index" class="group hover:bg-gray-50 transition-colors">
                                                <td v-if="hasBarcodes" class="px-3 py-2 align-top">
                                                    <input v-model="editForm.line_items[index].barcode" class="form-input text-center font-mono" placeholder="0002" @input.stop />
                                                </td>
                                                <td class="px-3 py-2 align-top">
                                                    <input v-model="editForm.line_items[index].quantity" class="form-input text-center" placeholder="1" @input.stop />
                                                </td>
                                                <td class="px-3 py-2 align-top">
                                                    <input v-model="editForm.line_items[index].unit" class="form-input text-center" placeholder="pcs" @input.stop />
                                                </td>
                                                <td class="px-3 py-2 align-top">
                                                    <input v-model="editForm.line_items[index].description" class="form-input" placeholder="Item description" @input.stop />
                                                </td>
                                                <td class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].unit_price" class="form-input text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td v-if="hasNetPrice" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].net_price" class="form-input text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td v-if="hasNetQuantity" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].unit_price_per_item" class="form-input text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td v-if="hasNetQuantity" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].net_quantity" class="form-input text-right" placeholder="1" @input.stop />
                                                </td>
                                                <td v-if="hasNetAmount" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].net_amount" class="form-input text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td v-if="hasDiscounts" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].discount" class="form-input text-right" placeholder="0" @input.stop />
                                                </td>
                                                <td v-if="hasDiscounts" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].discount_percentage" class="form-input text-right" placeholder="%" @input.stop />
                                                </td>
                                                <td v-if="hasTaxInfo" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].tax_rate" class="form-input text-right" placeholder="20" @input.stop />
                                                </td>
                                                <td v-if="hasTaxInfo" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].tax_amount" class="form-input text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td v-if="hasExciseDuty" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].excise_duty_amount" class="form-input text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td class="px-3 py-2 align-top text-right font-medium">
                                                    <input v-model="editForm.line_items[index].total" class="form-input form-input-bold text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td v-if="hasTotalWithTax" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].total_with_tax" class="form-input text-right" placeholder="0.00" @input.stop />
                                                </td>
                                                <td v-if="hasCurrencies" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].currency" class="form-input text-right" :placeholder="displayCurrency || 'EUR'" @input.stop />
                                                </td>
                                                <td v-if="hasBatchNumbers" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].batch_number" class="form-input text-right font-mono text-xs" placeholder="Batch" @input.stop />
                                                </td>
                                                <td v-if="hasExpiryDates" class="px-3 py-2 align-top text-right">
                                                    <input v-model="editForm.line_items[index].expiry_date" class="form-input text-right" placeholder="YYYY-MM-DD" @input.stop />
                                                </td>
                                                <td class="px-3 py-2 align-top text-center">
                                                    <button type="button" @click="removeLineItem(index)" class="text-gray-400 hover:text-red-500 transition-colors" style="font-size: 1.5rem;">
                                                        &times;
                                                    </button>
                                                </td>
                                            </tr>
                                        </template>
                                        <tr v-else>
                                            <td :colspan="7 + (hasBarcodes ? 1 : 0) + (hasNetPrice ? 1 : 0) + (hasNetQuantity ? 2 : 0) + (hasNetAmount ? 1 : 0) + (hasDiscounts ? 2 : 0) + (hasTaxInfo ? 2 : 0) + (hasExciseDuty ? 1 : 0) + (hasTotalWithTax ? 1 : 0) + (hasBatchNumbers ? 1 : 0) + (hasExpiryDates ? 1 : 0) + (hasCurrencies ? 1 : 0)" class="px-3 py-8 text-center text-gray-400 italic">{{ $t('scanDetails.noLineItems') }}</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Payment Details & Notes -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- Payment Details -->
                                <div class="form-section">
                                    <h3 class="form-section-title">{{ $t('scanDetails.paymentDetails') }}</h3>
                                    <div>
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.paymentMethod') }}</label>
                                            <SelectField v-model="editForm.payment_method" :options="paymentmethodOptions" class="form-input" />
                                        </div>
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.bankName') }}</label>
                                            <input v-model="editForm.payment_bank_name" class="form-input" @input.stop />
                                        </div>
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.iban') }}</label>
                                            <input v-model="editForm.payment_iban" class="form-input font-mono" @input.stop />
                                        </div>
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.swift') }}</label>
                                            <input v-model="editForm.payment_swift_bic" class="form-input font-mono" @input.stop />
                                        </div>
                                    </div>
                                </div>

                                <!-- Right column: Warehouse + Vendor Code + Notes -->
                                <div class="space-y-4">
                                    <!-- Warehouse -->
                                    <div v-if="authStore.useWarehouse" class="form-section">
                                        <h3 class="form-section-title">{{ $t('scanDetails.warehouseSection') }}</h3>
                                        <div class="form-group">
                                            <label class="form-label">{{ $t('scanDetails.warehouse') }}</label>
                                            <input v-model="editForm.warehouse_number" type="number" min="1" class="form-input" @input.stop />
                                        </div>
                                    </div>

                                    <!-- Notes -->
                                    <div class="form-section">
                                        <h3 class="form-section-title">{{ $t('scanDetails.notes') }}</h3>
                                        <div class="form-group">
                                            <textarea v-model="editForm.notes" rows="7" class="form-input resize-none" :placeholder="$t('scanDetails.notesPlaceholder')" @input.stop></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <!-- Inline Actions for Parties (Only show when not editing and parties missing/unsaved) -->
                        <div class="mt-6 space-y-4">
                            <div v-if="!scan.vendor_id && hasSellerField('name')" class="p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                                <div>
                                    <h4 class="text-sm font-bold text-amber-800">{{ $t('scanDetails.unknownSeller') }}</h4>
                                    <p class="text-xs text-amber-700">{{ $t('scanDetails.unknownSellerDesc') }}</p>
                                </div>
                                <div class="flex gap-2 w-full md:w-auto">
                                    <button type="button" @click="openSaveSellerModal" class="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 transition-colors w-full md:w-auto">{{ $t('scanDetails.saveVendor') }}</button>
                                </div>
                            </div>

                            <div v-if="!scan.company_id && hasBuyerField('name')" class="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                                <div>
                                    <h4 class="text-sm font-bold text-emerald-800">{{ $t('scanDetails.unknownBuyer') }}</h4>
                                    <p class="text-xs text-emerald-700">{{ $t('scanDetails.unknownBuyerDesc') }}</p>
                                </div>
                                <div class="flex gap-2 w-full md:w-auto">
                                    <button type="button" @click="openSaveBuyerModal" class="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded hover:bg-emerald-600 transition-colors w-full md:w-auto">{{ $t('scanDetails.saveCompany') }}</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ANALYSIS TAB -->
                    <div v-if="activeTab === 'analysis'" class="p-6 space-y-4">
                        <!-- Document Code -->
                        <div v-if="scan.document_code" class="form-section">
                            <div class="flex justify-between items-start mb-3">
                                <h3 class="form-section-title mb-0">{{ $t('scanDetails.docClassification') }}</h3>
                                <span class="px-2 py-1 rounded text-xs font-bold uppercase" :class="getConfidenceClass(scan.document_code_confidence)" style="background: #dbeafe; color: #1e40af;">
                  {{ scan.document_code_confidence }}% {{ $t('scanDetails.confidence') }}
                </span>
                            </div>
                            <div class="mb-3">
                                <span class="text-2xl font-bold" style="color: #1e3a8a;">{{ scan.document_code }}</span>
                            </div>
                            <div class="space-y-2 text-sm" style="color: #1e40af;">
                                <div v-if="scan.document_code_group" class="form-group">
                                    <label class="form-label">{{ $t('scanDetails.group') }}</label>
                                    <div class="form-input" style="background: #eff6ff;">{{ scan.document_code_group }}</div>
                                </div>
                                <div v-if="scan.document_code_description" class="form-group">
                                    <label class="form-label">{{ $t('scanDetails.description') }}</label>
                                    <div class="form-input" style="background: #eff6ff;">{{ scan.document_code_description }}</div>
                                </div>
                                <div v-if="scan.document_code_reasoning" class="italic p-3 rounded" style="background: #eff6ff; color: #1e40af;">
                                    {{ scan.document_code_reasoning }}
                                </div>
                            </div>
                        </div>

                        <!-- Document Type -->
                        <div v-if="scan.document_type" class="form-section" style="background: #d1fae5;">
                            <div class="flex justify-between items-start mb-3">
                                <h3 class="form-section-title mb-0" style="color: #065f46;">{{ $t('scanDetails.typeClassification') }}</h3>
                                <span class="px-2 py-1 rounded text-xs font-bold uppercase" style="background: #a7f3d0; color: #065f46;">
                  {{ scan.document_type_confidence }}% {{ $t('scanDetails.confidence') }}
                </span>
                            </div>
                            <div class="mb-3">
                                <span class="text-xl font-bold" style="color: #064e3b;">{{ scan.document_type }}</span>
                            </div>
                            <div class="space-y-2 text-sm" style="color: #065f46;">
                                <div v-if="scan.document_type_name" class="form-group">
                                    <label class="form-label" style="color: #065f46;">{{ $t('scanDetails.name') }}</label>
                                    <div class="form-input" style="background: #ecfdf5;">{{ scan.document_type_name }}</div>
                                </div>
                                <div v-if="scan.document_type_is_cash !== null" class="form-group">
                                    <label class="form-label" style="color: #065f46;">{{ $t('scanDetails.accounting') }}</label>
                                    <div class="form-input" :class="scan.document_type_is_cash ? 'bg-amber-100' : 'bg-blue-100'">
                                        {{ scan.document_type_is_cash ? $t('scanDetails.cash') : $t('scanDetails.accrual') }}
                                    </div>
                                </div>
                                <div v-if="scan.document_type_reasoning" class="italic p-3 rounded" style="background: #ecfdf5; color: #065f46;">
                                    {{ scan.document_type_reasoning }}
                                </div>
                            </div>
                        </div>

                        <!-- Additional Notes -->
                        <div class="form-section">
                            <h3 class="form-section-title">{{ $t('scanDetails.additionalNotes') }}</h3>

                            <div class="form-group">
                                <label class="form-label">{{ $t('scanDetails.compiledBy') }}</label>
                                <input v-model="editForm.compiled_by" type="text" class="form-input" />
                            </div>

                            <div class="form-group">
                                <label class="form-label">{{ $t('scanDetails.notes') }}</label>
                                <textarea v-model="editForm.notes" rows="4" class="form-input resize-none" placeholder="Additional notes..."></textarea>
                            </div>
                        </div>
                    </div>

                </div>

                <div class="scan-details-footer">
                    <div class="scan-details-footer-inner">
                        <!-- First Row: Navigation Buttons and Checkbox (Desktop) -->
                        <div class="hidden lg:flex items-center justify-between w-full gap-3">
                            <!-- Navigation Buttons -->
                            <div class="scan-details-nav-buttons">
                                <button type="button" @click="navigateToPrevious" :disabled="!previousInvoiceId" class="scan-details-nav-btn" title="Previous Invoice (←)">
                                    <LineIcon name="chevron-left" :size="14" :stroke-width="1.8" />
                                </button>
                                <button type="button" @click="navigateToNext" :disabled="!nextInvoiceId" class="scan-details-nav-btn" title="Next Invoice (→)">
                                    <LineIcon name="chevron-right" :size="14" :stroke-width="1.8" />
                                </button>
                            </div>

                            <!-- Checked Status -->
                            <label class="scan-details-checkbox-label">
                                <div class="scan-details-checkbox-text">
                                    <span class="scan-details-checkbox-text-top">{{ $t('scanDetails.check') }}</span>
                                    <span class="scan-details-checkbox-text-bottom">{{ $t('scanDetails.markAsChecked') }}</span>
                                </div>
                                <div class="scan-details-checkbox-container">
                                    <input type="checkbox" v-model="editForm.is_checked" class="scan-details-checkbox" />
                                    <LineIcon name="check" :size="15" :stroke-width="1.8" class="scan-details-checkbox-icon" />
                                </div>
                            </label>
                        </div>

                        <!-- First Row: Navigation Buttons (Mobile Only) -->
                        <div class="flex lg:hidden justify-center w-full mb-3">
                            <div class="scan-details-nav-buttons">
                                <button type="button" @click="navigateToPrevious" :disabled="!previousInvoiceId" class="scan-details-nav-btn" title="Previous Invoice (←)">
                                    <LineIcon name="chevron-left" :size="14" :stroke-width="1.8" />
                                </button>
                                <button type="button" @click="navigateToNext" :disabled="!nextInvoiceId" class="scan-details-nav-btn" title="Next Invoice (→)">
                                    <LineIcon name="chevron-right" :size="14" :stroke-width="1.8" />
                                </button>
                            </div>
                        </div>

                        <!-- Second Row: Total, VAT Amount, and Action Buttons -->
                        <div class="flex flex-row flex-wrap lg:flex-nowrap items-stretch lg:items-end justify-start gap-3 lg:gap-4 w-full">
                            <!-- Total -->
                            <div class="scan-details-amount-container">
                                <span class="scan-details-label">{{ $t('scanDetails.total') }}</span>
                                <div>
                                    <input v-model="editForm.grand_total" type="text" :class="['scan-details-total-input', missingFields.has('grand_total') ? 'border-red-500 bg-red-50' : '', totalMismatch ? 'border-orange-500 bg-orange-50' : '']" placeholder="0.00" @input.stop />
                                    <span class="scan-details-currency">{{ editForm.currency }}</span>
                                </div>
                            </div>

                            <!-- VAT Amount -->
                            <div class="scan-details-amount-container">
                                <span class="scan-details-label">{{ $t('scanDetails.vatAmount') }}</span>
                                <div>
                                    <input v-model="editForm.tax_amount" type="text" :class="['scan-details-amount-input', missingFields.has('tax_amount') ? 'border-red-500 bg-red-50' : '']" placeholder="0.00" @input.stop />
                                    <span class="scan-details-currency">{{ editForm.currency }}</span>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="scan-details-buttons w-full lg:w-auto hidden lg:flex">
                                <!-- Export Button -->
                                <button type="button"
                                        @click="showExportModal = true"
                                        class="btn btn-secondary scan-details-btn flex-1 lg:flex-initial">
                                    <LineIcon name="download" :size="15" :stroke-width="1.8" />
                                    <span class="scan-details-btn-text">{{ $t('scanDetails.export') }}</span>
                                </button>

                                <!-- Save Button -->
                                <button type="button"
                                        @click="saveEdit"
                                        class="btn btn-success scan-details-btn flex-1 lg:flex-initial">
                                    <LineIcon name="check" :size="15" :stroke-width="2" />
                                    <span class="scan-details-btn-text">{{ $t('common.save') }}</span>
                                </button>
                            </div>
                        </div>

                        <!-- Third Row: Checkbox and Save Button (Mobile Only) - Moved to Bottom -->
                        <div class="w-full flex lg:hidden gap-3 items-center order-last">
                            <!-- Checked Status -->
                            <label class="scan-details-checkbox-label flex-1 justify-center">
                                <div class="scan-details-checkbox-text">
                                    <span class="scan-details-checkbox-text-top">{{ $t('scanDetails.check') }}</span>
                                    <span class="scan-details-checkbox-text-bottom">{{ $t('scanDetails.markAsChecked') }}</span>
                                </div>
                                <div class="scan-details-checkbox-container">
                                    <input type="checkbox" v-model="editForm.is_checked" class="scan-details-checkbox" />
                                    <LineIcon name="check" :size="15" :stroke-width="1.8" class="scan-details-checkbox-icon" />
                                </div>
                            </label>

                            <!-- Save Button -->
                            <button type="button"
                                    @click="saveEdit"
                                    class="btn btn-success scan-details-btn w-32">
                                <LineIcon name="check" :size="15" :stroke-width="2" />
                                <span class="scan-details-btn-text">{{ $t('common.save') }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>



        <!-- Rescan Modal -->
        <RescanModal
            :show="showRescanModal"
            :loading="rescanLoading"
            :error="rescanError"
            :canUseHardScan="canUseHardScan"
            :hardScansRemaining="hardScansRemaining"
            :hardScanLimit="hardScanLimit"
            @close="closeRescanModal"
            @rescan="handleRescan"
        />

        <!-- Seller Selection Modal -->
        <SelectSellerModal
            :show="showSellerModal"
            :company-id="scan?.company_id"
            @close="closeSellerModal"
            @select="handleSellerSelection"
        />

        <!-- Buyer Selection Modal -->
        <SelectBuyerModal
            :show="showBuyerModal"
            @close="closeBuyerModal"
            @select="handleBuyerSelection"
        />

        <!-- Export Modal -->
        <ExportModal
            v-if="showExportModal"
            :key="`export-${scanId}-${exportModalKey}`"
            :invoice-id="scanId"
            :company-id="scan?.company_id"
            @close="showExportModal = false"
            @exported="handleExported"
            @updated="handleInvoiceUpdated"
        />
    </div>
</template>

<script setup>
import SelectField from '@/components/SelectField.vue';
import { ref, onMounted, onUnmounted, computed, watch, nextTick, unref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useScanStore } from '@/stores/scan';
import { useUiStore } from '@/stores/ui';
import { useToast } from 'vue-toast-notification';
import api from '@/utils/api';
import VueMagnifier from '@websitebeaver/vue-magnifier';
import '@websitebeaver/vue-magnifier/styles.css';
import { useCompanies, useVendors } from '@/composables/useCompanyVendor';
import { useAuthStore } from '@/stores/auth';
import { useExport } from '@/composables/useExport';
import { useRescan } from '@/composables/useRescan';
import LineIcon from '@/components/icons/LineIcon.vue';
import CompanySelector from '@/components/selectors/CompanySelector.vue';
import VendorSelector from '@/components/selectors/VendorSelector.vue';
import TypeSelector from '@/components/selectors/TypeSelector.vue';
import ExportModal from '@/components/scanner/ExportModal.vue';

import RescanModal from '@/components/modals/RescanModal.vue';
import VendorCompanyModal from '@/components/modals/VendorCompanyModal.vue';
import SelectSellerModal from '@/components/modals/SelectSellerModal.vue';
import SelectBuyerModal from '@/components/modals/SelectBuyerModal.vue';
import { useI18n } from 'vue-i18n';
import { hasMissingInfo } from '@/services/exportValidationService';
import { ensureBgPrefix } from '@/utils/vatHelpers';

const route = useRoute();
const router = useRouter();
const scanStore = useScanStore();
const authStore = useAuthStore();
const $toast = useToast();

// Rescan functionality
const {
    showRescanModal,
    rescanLoading,
    rescanError,
    hardScansRemaining,
    hardScanLimit,
    canUseHardScan,
    openRescanModal,
    closeRescanModal,
    performRescan
} = useRescan();

// Export modal
const showExportModal = ref(false);
const showQuickExportDropdown = ref(false);
const exportModalKey = ref(0); // Key to force modal remount

// Use export composable
const {
    showExportDropdown,
    exporting,
    exportError,
    exportFormats,
    exportSingle,
    toggleExportDropdown: _toggleExportDropdown,
    closeExportDropdown
} = useExport();

// VAT validation computed properties
const sellerVatValidation = computed(() => {
    if (!scan.value) return null;

    // Check root level first
    if ('seller_vat_number_valid' in scan.value) {
        return scan.value.seller_vat_number_valid;
    }

    // Check inside result object
    if (scan.value.result && 'seller_vat_number_valid' in scan.value.result) {
        return scan.value.result.seller_vat_number_valid;
    }

    return null;
});

const buyerVatValidation = computed(() => {
    if (!scan.value) return null;

    // Check root level first
    if ('buyer_vat_number_valid' in scan.value) {
        return scan.value.buyer_vat_number_valid;
    }

    // Check inside result object
    if (scan.value.result && 'buyer_vat_number_valid' in scan.value.result) {
        return scan.value.result.buyer_vat_number_valid;
    }

    return null;
});

const getVatInputClass = (isValid) => {
    if (isValid === null) return 'form-input';
    return isValid ? 'form-input !border-2 !border-green-500' : 'form-input !border-2 !border-red-500';
};

// Missing fields validation for visual indicators
const missingFields = computed(() => {
    if (!scan.value) return new Set();

    const missing = new Set();
    const type = scan.value.type;

    // Map invoice data to match validation service expectations
    const invoiceData = {
        invoice_number: editForm.value?.invoice_number || '',
        company_vat: type === 'expense' ? (editForm.value?.buyer_vat_number || '') : (editForm.value?.seller_vat_number || ''),
        vendor_vat: type === 'expense' ? (editForm.value?.seller_vat_number || '') : (editForm.value?.buyer_vat_number || ''),
        vendor_name: type === 'expense' ? (editForm.value?.seller_name || '') : (editForm.value?.buyer_name || ''),
        currency: editForm.value?.currency || '',
        vat_amount: editForm.value?.tax_amount || '',
        total_amount: editForm.value?.grand_total || ''
    };

    // Check each required field
    if (!invoiceData.invoice_number || invoiceData.invoice_number === '') {
        missing.add('invoice_number');
    }
    // VAT numbers are no longer mandatory
    // if (!invoiceData.company_vat || invoiceData.company_vat === '') {
    //   missing.add(type === 'expense' ? 'buyer_vat_number' : 'seller_vat_number');
    // }
    // if (!invoiceData.vendor_vat || invoiceData.vendor_vat === '') {
    //   missing.add(type === 'expense' ? 'seller_vat_number' : 'buyer_vat_number');
    // }
    if (!invoiceData.vendor_name || invoiceData.vendor_name === '') {
        missing.add(type === 'expense' ? 'seller_name' : 'buyer_name');
    }
    if (!invoiceData.currency || invoiceData.currency === '') {
        missing.add('currency');
    }
    // VAT amount is no longer mandatory
    // if (!invoiceData.vat_amount || invoiceData.vat_amount === '') {
    //   missing.add('tax_amount');
    // }
    if (!invoiceData.total_amount || invoiceData.total_amount === '') {
        missing.add('grand_total');
    }

    return missing;
});

// Calculate total from line items
const lineItemsCalculatedTotal = computed(() => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return 0;
    }

    const subtotal = parseFloat(editForm.value?.subtotal) || 0;

    // Sum line item totals — these are the final amounts as shown on the invoice (including VAT/discounts)
    const rawTotal = editForm.value.line_items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const rawRounded = Math.round(rawTotal * 100) / 100;
    const subtotalRounded = Math.round(subtotal * 100) / 100;

    // If line totals match subtotal (pre-VAT amounts), add VAT to get grand total
    if (subtotal > 0 && Math.abs(rawRounded - subtotalRounded) < 0.02) {
        const vatAmount = parseFloat(editForm.value?.tax_amount) || 0;
        return Math.round((rawTotal + vatAmount) * 100) / 100;
    }

    // Some documents print the line at its pre-discount amount and the deduction on
    // a line of its own — a fuel receipt shows 86.42 for the litres, 1.62 of loyalty
    // discount below it, and 84.80 to pay. Summing the line totals alone overshoots
    // by the discount and reports a mismatch on correct data. Only applied when it
    // lands exactly on the stated total, so a discount already folded into the line
    // total is never subtracted twice. Mirrors Invoice::calculateLineItemsTotal().
    const discountTotal = editForm.value.line_items.reduce((sum, item) => {
        const discount = item.discount;
        if (discount === null || discount === undefined || discount === '' || String(discount).includes('%')) {
            return sum;
        }
        return sum + Math.abs(parseFloat(discount) || 0);
    }, 0);

    if (discountTotal > 0) {
        const grandTotal = Math.round((parseFloat(editForm.value?.grand_total) || 0) * 100) / 100;
        const netOfDiscounts = Math.round((rawRounded - discountTotal) * 100) / 100;

        if (grandTotal > 0 && Math.abs(netOfDiscounts - grandTotal) <= 0.01) {
            return netOfDiscounts;
        }
    }

    // Line items already represent final amounts (including VAT)
    return rawRounded;
});

// Check if line items total matches invoice total
const totalMismatch = computed(() => {
    if (!editForm.value?.line_items || editForm.value.line_items.length === 0) {
        return false; // No mismatch if no line items
    }

    const invoiceTotal = parseFloat(editForm.value?.grand_total) || 0;
    const itemsTotal = lineItemsCalculatedTotal.value;

    // Round difference to 2 decimal places to handle floating point precision
    const difference = Math.round(Math.abs(invoiceTotal - itemsTotal) * 100) / 100;

    console.log('Total Validation:', {
        invoiceTotal,
        itemsTotal,
        difference,
        willShowWarning: difference > 0.01
    });

    // Show warning for differences greater than 1 cent (allow 1 cent rounding tolerance)
    return difference > 0.01;
});

// Get the difference amount
const totalDifference = computed(() => {
    const invoiceTotal = parseFloat(editForm.value?.grand_total) || 0;
    const itemsTotal = lineItemsCalculatedTotal.value;
    return Math.round((invoiceTotal - itemsTotal) * 100) / 100;
});

// Helper: compute effective line items total (totals already include discounts)
const getLineItemsEffectiveTotal = () => {
    if (!editForm.value?.line_items) return 0;
    return editForm.value.line_items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
};

// Helper: line item totals with their separately-extracted discounts applied. On
// gross (VAT-inclusive) invoices this is what the grand total is built from —
// e.g. a fuel receipt printing 86.42 on the line and 1.62 of loyalty discount
// below it settles at 84.80. Percentage discounts ("5%") carry no amount, so
// they're skipped rather than parsed into a bogus 5.00.
const getLineItemsGrossTotal = () => {
    if (!editForm.value?.line_items) return 0;
    return editForm.value.line_items.reduce((sum, item) => {
        const total = parseFloat(item.total) || 0;
        const rawDiscount = item.discount;
        const isPercentage = typeof rawDiscount === 'string' && rawDiscount.includes('%');
        const discount = isPercentage ? 0 : Math.abs(parseFloat(rawDiscount) || 0);
        return sum + total - discount;
    }, 0);
};

// Check if line items total matches subtotal
const subtotalMismatch = computed(() => {
    if (!authStore.enforceSubtotalValidation) {
        return false;
    }

    if (!editForm.value?.line_items || editForm.value.line_items.length === 0) {
        return false;
    }
    const subtotal = parseFloat(editForm.value?.subtotal) || 0;
    if (subtotal === 0) return false;

    // Gross line items that already add up to the grand total are a valid
    // representation, not a mismatch — the invoice is internally consistent and
    // the lines must not be rewritten against the subtotal. Without this, every
    // VAT-inclusive invoice raises the banner and "Deduct VAT" overwrites
    // correctly extracted unit prices and line totals.
    //
    // Both discount conventions count as consistent: totals printed before the
    // discount (86.42 with a separate -1.62), and totals already net of it
    // (84.80). Checking only one would double-count the discount on the other.
    const grandTotal = parseFloat(editForm.value?.grand_total) || 0;
    if (grandTotal > 0) {
        const grossTotal = Math.round(getLineItemsEffectiveTotal() * 100) / 100;
        const grossLessDiscounts = Math.round(getLineItemsGrossTotal() * 100) / 100;
        if (Math.abs(grossTotal - grandTotal) <= 0.01 || Math.abs(grossLessDiscounts - grandTotal) <= 0.01) {
            return false;
        }
    }

    const itemsTotal = Math.round(getLineItemsEffectiveTotal() * 100) / 100;
    const subtotalRounded = Math.round(subtotal * 100) / 100;

    return Math.abs(itemsTotal - subtotalRounded) > 0.01;
});

const subtotalDifference = computed(() => {
    if (!editForm.value?.line_items) return { itemsTotal: 0, subtotal: 0, difference: 0 };
    const subtotal = parseFloat(editForm.value?.subtotal) || 0;
    const itemsTotal = getLineItemsEffectiveTotal();
    const itemsTotalRounded = Math.round(itemsTotal * 100) / 100;
    const subtotalRounded = Math.round(subtotal * 100) / 100;
    return {
        itemsTotal: itemsTotalRounded,
        subtotal: subtotalRounded,
        difference: Math.round((itemsTotalRounded - subtotalRounded) * 100) / 100
    };
});

// Whether the gap between the line items and the subtotal is the VAT itself —
// i.e. the lines carry VAT while the subtotal is net, so deducting it lands
// exactly on the subtotal. Any other gap (a misread digit on one line, a missing
// line) is not fixed by deducting VAT; doing so would rewrite the lines that were
// read correctly, so the button is hidden in that case.
const subtotalMismatchIsVatShaped = computed(() => {
    const subtotal = parseFloat(editForm.value?.subtotal) || 0;
    const vatAmount = parseFloat(editForm.value?.tax_amount) || 0;
    if (subtotal <= 0 || vatAmount <= 0) return false;

    const itemsTotal = getLineItemsEffectiveTotal();
    return Math.abs(itemsTotal - (subtotal + vatAmount)) <= 0.02;
});

const deductVatFromLineItems = () => {
    if (!editForm.value?.line_items) return;

    const subtotal = parseFloat(editForm.value?.subtotal) || 0;

    // First pass: deduct VAT from each item total and discount
    editForm.value.line_items.forEach(item => {
        const total = parseFloat(item.total) || 0;
        if (total === 0) return;

        const taxRate = parseFloat(item.tax_rate) || parseFloat(editForm.value?.tax_rate) || parseFloat(editForm.value?.vat_rate) || 20;
        const divisor = 1 + taxRate / 100;
        const netTotal = Math.round((total / divisor) * 100) / 100;
        const taxAmount = Math.round((total - netTotal) * 100) / 100;

        item.total = netTotal.toFixed(2);
        item.tax_amount = taxAmount.toFixed(2);

        const unitPrice = parseFloat(item.unit_price) || 0;
        if (unitPrice > 0) {
            const quantity = parseFloat(item.quantity) || 1;
            item.unit_price = (netTotal / quantity).toFixed(2);
        }
    });

    // Second pass: compute effective total (with discounts) and adjust to match subtotal
    if (subtotal > 0) {
        const effectiveTotal = Math.round(getLineItemsEffectiveTotal() * 100) / 100;
        const diff = Math.round((effectiveTotal - subtotal) * 100) / 100;

        if (diff !== 0) {
            const nonZeroItems = editForm.value.line_items.filter(item => (parseFloat(item.total) || 0) > 0);
            if (nonZeroItems.length > 0) {
                const nonZeroSum = nonZeroItems.reduce((s, item) => s + (parseFloat(item.total) || 0), 0);
                let remainingDiff = diff;
                nonZeroItems.forEach((item, idx) => {
                    const itemTotal = parseFloat(item.total) || 0;
                    let itemAdjustment;
                    if (idx === nonZeroItems.length - 1) {
                        itemAdjustment = remainingDiff;
                    } else {
                        itemAdjustment = Math.round((diff * (itemTotal / nonZeroSum)) * 100) / 100;
                    }
                    remainingDiff = Math.round((remainingDiff - itemAdjustment) * 100) / 100;
                    const adjusted = Math.round((itemTotal - itemAdjustment) * 100) / 100;
                    item.total = adjusted.toFixed(2);
                    item.tax_amount = (parseFloat(item.tax_amount || 0) + itemAdjustment).toFixed(2);
                    const quantity = parseFloat(item.quantity) || 1;
                    if (parseFloat(item.unit_price) > 0) {
                        item.unit_price = (adjusted / quantity).toFixed(2);
                    }
                });
            }
        }
    }
};

const getFieldClass = (fieldName) => {
    const baseClass = 'form-input';
    if (missingFields.value.has(fieldName)) {
        return `${baseClass} !border-2 !border-red-500 bg-red-50`;
    }
    return baseClass;
};

const toggleQuickExportDropdown = () => {
    showQuickExportDropdown.value = !showQuickExportDropdown.value;
};

const handleExported = () => {
    showExportModal.value = false;
    uiStore.showToast({ message: t('scanDetails.exportSuccess'), type: 'success' });
};

const handleInvoiceUpdated = async () => {
    // Close the modal temporarily
    showExportModal.value = false;

    // Reload the invoice data to get the updated values
    await loadScan();

    // Wait for DOM update
    await nextTick();

    // Increment key to force complete remount of modal with fresh data
    exportModalKey.value++;

    // Reopen the modal with fresh data
    showExportModal.value = true;
};

// Override toggleExportDropdown to close other dropdowns
const toggleExportDropdown = () => {
    // Close settings dropdown if open
    if (showSettingsDropdown.value) {
        closeSettingsDropdown();
    }
    _toggleExportDropdown();
};

const uiStore = useUiStore();
const { t } = useI18n();

const loading = ref(true);
const error = ref(null);
const scan = ref(null);
const thumbnailLoaded = ref(true);
const isEditing = ref(true); // Always editing
const editForm = ref({}); // Initialize empty
const saveVendorCompanyId = ref(null);
const paymentMethods = ref([]); // Payment methods from API
const documentTypes = ref([]); // Document types from API

const sellerExists = ref(false);
const buyerExists = ref(false);
const activeTab = ref('invoice');
const showSettingsDropdown = ref(false);

// Check if this invoice has barcodes (Bulgarian gas station invoice)
// Sticky column visibility - once a column appears, it stays visible during the session
const stickyColumns = ref({});
const makeStickyComputed = (name, computeFn) => {
    return computed(() => {
        const currentValue = computeFn();
        if (currentValue) {
            stickyColumns.value[name] = true;
        }
        return stickyColumns.value[name] || false;
    });
};

const hasBarcodes = makeStickyComputed('barcodes', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => item.barcode && item.barcode.toString().trim() !== '');
});

const hasBatchNumbers = makeStickyComputed('batchNumbers', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => item.batch_number && item.batch_number.trim() !== '');
});

const hasExpiryDates = makeStickyComputed('expiryDates', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => item.expiry_date && item.expiry_date.trim() !== '');
});

const hasCurrencies = makeStickyComputed('currencies', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => item.currency && item.currency.trim() !== '');
});

// The currency to show against the line-item amounts. Line items rarely carry their
// own currency (so the per-item currency column stays hidden); fall back to the
// invoice-level currency so the amounts still show which currency they are in.
const displayCurrency = computed(() => {
    const c = (editForm.value?.currency || '').trim();
    if (c) return c;
    const item = (editForm.value?.line_items || []).find(i => i.currency && i.currency.trim() !== '');
    return item ? item.currency.trim() : '';
});

const hasDiscounts = makeStickyComputed('discounts', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => {
        const discount = item.discount;
        if (discount !== null && discount !== undefined) {
            const trimmed = discount.toString().trim();
            if (trimmed !== '' && trimmed !== '0' && trimmed !== '0.00' && trimmed !== 'null') {
                return true;
            }
        }
        const pct = item.discount_percentage;
        if (pct !== null && pct !== undefined) {
            const trimmedPct = pct.toString().trim();
            if (trimmedPct !== '' && trimmedPct !== '0' && trimmedPct !== 'null') {
                return true;
            }
        }
        return false;
    });
});

const hasNetQuantity = makeStickyComputed('netQuantity', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => {
        const nq = item.net_quantity;
        return nq && nq.toString().trim() !== '' && nq.toString().trim() !== '1';
    });
});

const hasNetAmount = makeStickyComputed('netAmount', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => {
        const na = item.net_amount;
        return na && na.toString().trim() !== '' && na.toString().trim() !== '0' && na.toString().trim() !== '0.00';
    });
});

const hasTaxInfo = makeStickyComputed('taxInfo', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => {
        const taxAmount = item.tax_amount;
        const taxRate = item.tax_rate;
        return (taxAmount && taxAmount.toString().trim() !== '' && taxAmount.toString().trim() !== '0' && taxAmount.toString().trim() !== '0.00') ||
            (taxRate && taxRate.toString().trim() !== '' && taxRate.toString().trim() !== '0');
    });
});

const hasExciseDuty = makeStickyComputed('exciseDuty', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => {
        const excise = item.excise_duty_amount;
        return excise && excise.toString().trim() !== '' && excise.toString().trim() !== '0' && excise.toString().trim() !== '0.00';
    });
});

const hasTotalWithTax = makeStickyComputed('totalWithTax', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => {
        const totalWithTax = item.total_with_tax;
        return totalWithTax && totalWithTax.toString().trim() !== '' && totalWithTax.toString().trim() !== '0' && totalWithTax.toString().trim() !== '0.00';
    });
});

const hasNetPrice = makeStickyComputed('netPrice', () => {
    if (!editForm.value?.line_items || !Array.isArray(editForm.value.line_items) || editForm.value.line_items.length === 0) {
        return false;
    }
    return editForm.value.line_items.some(item => {
        const netPrice = item.net_price;
        return netPrice && netPrice.toString().trim() !== '' && netPrice.toString().trim() !== '0' && netPrice.toString().trim() !== '0.00';
    });
});

// Vendor/Company selection modal (legacy, kept for compatibility)
const showVendorModal = ref(false);
const vendorModalTarget = ref(null); // 'seller' or 'buyer'

// Separate modals for seller and buyer
const showSellerModal = ref(false);
const showBuyerModal = ref(false);

const tabs = [
    { id: 'invoice', label: t('scanDetails.invoiceTab') },
    { id: 'analysis', label: t('scanDetails.analysisTab') }
];

// Image Viewer State
const scale = ref(0.75); // Default 75% zoom
const rotation = ref(0);
const isDragging = ref(false);
const startX = ref(0);
const startY = ref(0);
const translateX = ref(0);
const translateY = ref(0);
const previewContainer = ref(null);
const imageElement = ref(null);
const rotatedImageUrl = ref(null);
const originalImageUrl = computed(() => `/api/invoices/${scanId.value}/original`);

const scanId = computed(() => route.params.id);

// Navigation state
const previousInvoiceId = ref(null);
const nextInvoiceId = ref(null);

// Image Viewer Methods
const zoomIn = () => {
    scale.value = Math.min(scale.value + 0.25, 3);
    applyTransform();
};

const zoomOut = () => {
    scale.value = Math.max(scale.value - 0.25, 0.5);
    applyTransform();
};

const applyTransform = () => {
    if (imageElement.value) {
        imageElement.value.style.transform = `scale(${scale.value}) translate(${translateX.value / scale.value}px, ${translateY.value / scale.value}px)`;
    }
};

const rotateLeft = () => {
    rotation.value -= 90;
    translateX.value = 0;
    translateY.value = 0;
    applyTransform();
    rotateImage();
};

const rotateRight = () => {
    rotation.value += 90;
    translateX.value = 0;
    translateY.value = 0;
    applyTransform();
    rotateImage();
};

const resetView = () => {
    scale.value = 1;
    rotation.value = 0;
    translateX.value = 0;
    translateY.value = 0;
    rotatedImageUrl.value = null;
    applyTransform();
};

const handleWheel = (e) => {
    if (e.ctrlKey) {
        if (e.deltaY < 0) zoomIn();
        else zoomOut();
    }
};

const startDrag = (e) => {
    isDragging.value = true;
    // Support both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
    startX.value = clientX - translateX.value;
    startY.value = clientY - translateY.value;
    if (imageElement.value) {
        imageElement.value.style.cursor = 'grabbing';
    }
};

const onDrag = (e) => {
    if (!isDragging.value) return;
    e.preventDefault();
    // Support both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;
    translateX.value = clientX - startX.value;
    translateY.value = clientY - startY.value;

    if (imageElement.value) {
        imageElement.value.style.transform = `scale(${scale.value}) translate(${translateX.value / scale.value}px, ${translateY.value / scale.value}px)`;
    }
};

const stopDrag = () => {
    isDragging.value = false;
    if (imageElement.value) {
        imageElement.value.style.cursor = 'grab';
    }
};

const rotateImage = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Normalize rotation to 0-360
        const normalizedRotation = ((rotation.value % 360) + 360) % 360;

        // For 90 or 270 degrees, swap width and height
        if (normalizedRotation === 90 || normalizedRotation === 270) {
            canvas.width = img.height;
            canvas.height = img.width;
        } else {
            canvas.width = img.width;
            canvas.height = img.height;
        }

        // Move to center, rotate, then draw
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((normalizedRotation * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        // Convert to base64
        rotatedImageUrl.value = canvas.toDataURL('image/jpeg', 0.95);
    };
    img.src = originalImageUrl.value;
};

// Use composables for company/vendor management
const { companies, loadCompanies, createCompany } = useCompanies();
const companyIdRef = computed(() => editForm.value?.company_id || null);
const { vendors: availableVendors, loadVendors } = useVendors(companyIdRef);

const selectedCompany = computed(() => {
    const companyId = editForm.value?.company_id;
    if (!companyId) {
        return null;
    }

    return companies.value.find((company) => company.id === companyId) || null;
});



const hasPaymentInfo = computed(() => {
    if (!scan.value?.result) return false;
    const r = scan.value.result;
    const payment = r.payment || {};
    return !!(
        r.payment_amount_due || payment.amount_due ||
        r.payment_method || payment.method ||
        r.payment_bank_name || payment.bank_details?.bank_name ||
        r.payment_iban || payment.bank_details?.iban ||
        r.payment_swift_bic || payment.bank_details?.swift_bic ||
        r.payment_account_number || payment.bank_details?.account_number
    );
});

const hasBankDetails = computed(() => {
    if (!scan.value?.result) return false;
    const r = scan.value.result;
    const payment = r.payment || {};
    return !!(
        r.payment_bank_name || payment.bank_details?.bank_name ||
        r.payment_iban || payment.bank_details?.iban ||
        r.payment_swift_bic || payment.bank_details?.swift_bic ||
        r.payment_account_number || payment.bank_details?.account_number
    );
});

const goBack = () => {
    router.push('/scans');
};

const loadScan = async () => {
    loading.value = true;
    error.value = null;

    console.log('loadScan - starting to load invoice:', scanId.value);

    try {
        const response = await api.get(`/api/invoices/${scanId.value}`);
        scan.value = response.data;

        console.log('loadScan - invoice loaded, vendor_code in result:', response.data.result?.vendor_code);

        // Store payment methods from API
        if (response.data.payment_methods) {
            paymentMethods.value = response.data.payment_methods;
        }

        // Initialize edit form
        initializeEditForm();

        // Load adjacent invoices for navigation
        await loadAdjacentInvoices();

        // Apply initial transform after DOM updates
        nextTick(() => {
            applyTransform();
        });
    } catch (err) {
        // Handle 404 - invoice not found
        if (err.response?.status === 404) {
            error.value = 'Invoice not found. It may have been deleted.';
            uiStore.showToast({ 
                message: 'Invoice not found. Redirecting to invoices list...', 
                type: 'error',
                duration: 3000 
            });
            setTimeout(() => {
                router.push('/scans');
            }, 2000);
        } else {
            error.value = err.response?.data?.message || 'Failed to load invoice details';
        }
        console.error('Failed to load scan:', err);
    } finally {
        loading.value = false;
    }
};

const loadAdjacentInvoices = async () => {
    try {
        const companyId = scan.value?.company_id;
        console.log('Loading adjacent invoices for company:', companyId);

        if (!companyId) {
            // No company, can't fetch adjacent invoices
            console.log('No company ID, disabling navigation');
            previousInvoiceId.value = null;
            nextInvoiceId.value = null;
            return;
        }

        // Fetch invoices for this company using the companies endpoint
        const response = await api.get(`/api/companies/${companyId}/invoices`, {
            params: {
                per_page: 1000,
                sort_by: 'created_at',
                sort_order: 'desc'
            }
        });

        console.log('Adjacent invoices response:', response.data);

        const invoices = response.data.data || response.data;
        if (!Array.isArray(invoices)) {
            console.error('Invalid response format:', response.data);
            previousInvoiceId.value = null;
            nextInvoiceId.value = null;
            return;
        }

        console.log(`Found ${invoices.length} invoices for company`);
        const currentIndex = invoices.findIndex(inv => inv.id == scanId.value);
        console.log('Current invoice index:', currentIndex);

        if (currentIndex > 0) {
            nextInvoiceId.value = invoices[currentIndex - 1].id;
            console.log('Next invoice ID:', nextInvoiceId.value);
        } else {
            nextInvoiceId.value = null;
            console.log('No next invoice (at newest)');
        }

        if (currentIndex < invoices.length - 1 && currentIndex !== -1) {
            previousInvoiceId.value = invoices[currentIndex + 1].id;
            console.log('Previous invoice ID:', previousInvoiceId.value);
        } else {
            previousInvoiceId.value = null;
            console.log('No previous invoice (at oldest)');
        }
    } catch (err) {
        console.error('Failed to load adjacent invoices:', err);
        previousInvoiceId.value = null;
        nextInvoiceId.value = null;
    }
};

// Load document types from API
const loadDocumentTypes = async () => {
    try {
        const response = await api.get('/api/invoices/document-types');
        documentTypes.value = response.data.document_types || [];
    } catch (err) {
        console.error('Failed to load document types:', err);
        // Fallback to empty array if API fails
        documentTypes.value = [];
    }
};

// Get translated label for document type
const getDocumentTypeLabel = (docType) => {
    // Map document type values to translation keys
    const typeMap = {
        'Invoice': 'invoice',
        'Credit Note': 'creditNote',
        'Debit Note': 'debitNote',
        'Protocol': 'protocol',
        'Stock Receipt': 'stockReceipt',
        'Purchase Order': 'purchaseOrder',
        'Sales Receipt': 'salesReceipt',
        'Bank Statement': 'bankStatement',
        'Balance Sheet': 'balanceSheet',
        'Income Statement (Profit and Loss Statement)': 'incomeStatement',
        'Cash Flow Statement': 'cashFlowStatement',
        'Tax Declaration': 'taxDeclaration',
        'Contract': 'contract',
        'Inventory Report': 'inventoryReport',
        'Expense Report': 'expenseReport',
        'Payroll Register': 'payrollRegister',
        'Audit Report': 'auditReport',
        'Waybill (or Bill of Lading)': 'waybill',
        'Reconciliation Statement': 'reconciliationStatement',
        'Receipt': 'receipt',
        'Cash Receipt': 'cashReceipt',
        'Proforma Invoice': 'proformaInvoice',
        'Quotation': 'quotation',
        'Journal Voucher': 'journalVoucher',
        'Ledger Entry': 'ledgerEntry',
        'Financial Statement': 'financialStatement',
        'Budget Report': 'budgetReport',
        'Trial Balance': 'trialBalance',
        'Other Document': 'otherDocument'
    };

    const key = typeMap[docType];
    return key ? t(`scanDetails.documentTypes.${key}`) : docType;
};

const initializeEditForm = () => {
    if (!scan.value) return;

    // If result is null or empty and editForm already has data, don't reinitialize
    // This preserves data during rescan when result is temporarily null
    if ((!scan.value.result || Object.keys(scan.value.result).length === 0) &&
        editForm.value && Object.keys(editForm.value).length > 0) {
        // Only update root-level fields
        editForm.value.company_id = scan.value.company_id || editForm.value.company_id;
        editForm.value.vendor_id = scan.value.vendor_id || editForm.value.vendor_id;
        editForm.value.type = scan.value.type || editForm.value.type;
        editForm.value.is_checked = scan.value.is_checked === true || scan.value.is_checked === 1;
        return;
    }

    stickyColumns.value = {}; // Reset column visibility for new scan
    editForm.value = JSON.parse(JSON.stringify(scan.value.result || {}));

    console.log('initializeEditForm - vendor_code from result:', scan.value.result?.vendor_code);
    console.log('initializeEditForm - editForm.vendor_code:', editForm.value.vendor_code);

    // Clean up invoice_number — remove spaces
    if (editForm.value.invoice_number) {
        editForm.value.invoice_number = editForm.value.invoice_number.replace(/\s+/g, '');
    }

    // Add company, vendor, type, is_checked fields from root level (not from result)
    editForm.value.company_id = scan.value.company_id || null;
    editForm.value.vendor_id = scan.value.vendor_id || null;
    editForm.value.type = scan.value.type || null;
    // is_checked comes from the root level of the API response, not from result
    editForm.value.is_checked = scan.value.is_checked === true || scan.value.is_checked === 1;

    // Copy tax_id fields from root level if they exist there but not in result
    if (scan.value.seller_tax_id && !editForm.value.seller_tax_id) {
        editForm.value.seller_tax_id = scan.value.seller_tax_id;
    }
    if (scan.value.buyer_tax_id && !editForm.value.buyer_tax_id) {
        editForm.value.buyer_tax_id = scan.value.buyer_tax_id;
    }

    // Ensure line items exist and is an array
    if (!editForm.value.line_items || !Array.isArray(editForm.value.line_items)) {
        editForm.value.line_items = [];
    }

    // Line item totals are used as-is from the AI extraction (discounts already factored in)

    // Add BG prefix to VAT numbers
    if (editForm.value.seller_vat_number) {
        editForm.value.seller_vat_number = ensureBgPrefix(editForm.value.seller_vat_number);
    }
    if (editForm.value.buyer_vat_number) {
        editForm.value.buyer_vat_number = ensureBgPrefix(editForm.value.buyer_vat_number);
    }

    // Set default warehouse number if not present
    if (!editForm.value.warehouse_number) {
        editForm.value.warehouse_number = 1;
    }

    // Populate vendor_code from linked vendor if not already in result
    // Priority: invoice result > vendor table
    if (!editForm.value.vendor_code && scan.value.vendor?.vendor_code) {
        editForm.value.vendor_code = scan.value.vendor.vendor_code;
    }
    
    console.log('initializeEditForm - final editForm.vendor_code:', editForm.value.vendor_code);
};

const checkPartyExists = async () => {
    const sellerVat = getSellerField('vat_number');
    const buyerVat = getBuyerField('vat_number');

    // Check if seller exists in companies or vendors
    if (sellerVat && companies.value.length > 0) {
        const allVendorsPromises = companies.value.map(company =>
            api.get(`/api/companies/${company.id}/vendors`).catch(() => ({ data: [] }))
        );

        const vendorsResponses = await Promise.all(allVendorsPromises);
        const allVendors = vendorsResponses.flatMap(r => r.data?.data || r.data || []);

        sellerExists.value = allVendors.some(v => v.vendor_vat === sellerVat) ||
            companies.value.some(c => c.vat_number === sellerVat);
    }

    // Check if buyer exists in companies
    if (buyerVat && companies.value.length > 0) {
        buyerExists.value = companies.value.some(c => c.vat_number === buyerVat);
    }
};

const handleCompanyChange = (company) => {
    // Vendor selector will auto-reload vendors via composable
    // Reset vendor selection when company changes
    if (editForm.value) {
        editForm.value.vendor_id = null;
    }
};

const saveAsVendor = async () => {
    if (!saveVendorCompanyId.value) {
        uiStore.showToast({ message: 'Please select a company', type: 'error' });
        return;
    }

    try {
        const vendorData = {
            vendor_name: getSellerField('name'),
            vendor_vat: getSellerField('vat_number'),
            vendor_address: getSellerField('address'),
            vendor_email: getSellerField('email'),
            vendor_phone: getSellerField('phone'),
            is_active: true
        };

        const response = await api.post(`/api/companies/${saveVendorCompanyId.value}/vendors`, vendorData);

        // Update the invoice with the new vendor
        const updateResponse = await api.put(`/api/invoices/${scanId.value}`, {
            company_id: saveVendorCompanyId.value,
            vendor_id: response.data.id
        });

        // Update scan with response data
        scan.value = updateResponse.data;

        // Reload vendors list if we're in a context where it matters
        if (saveVendorCompanyId.value) {
            await loadVendors(saveVendorCompanyId.value);
        }

        uiStore.showToast({ message: 'Vendor saved successfully', type: 'success' });
        saveVendorCompanyId.value = null;
    } catch (err) {
        uiStore.showToast({ message: 'Failed to save vendor', type: 'error' });
        console.error('Failed to save vendor:', err);
    }
};

const saveSellerAsCompany = async () => {
    try {
        const companyData = {
            name: getSellerField('name'),
            vat_number: getSellerField('vat_number'),
            address: getSellerField('address'),
            email: getSellerField('email'),
            phone: getSellerField('phone'),
            is_active: true
        };

        const newCompany = await createCompany(companyData);

        // Create vendor for this company
        const vendorData = {
            vendor_name: getSellerField('name'),
            vendor_vat: getSellerField('vat_number'),
            vendor_address: getSellerField('address'),
            vendor_email: getSellerField('email'),
            vendor_phone: getSellerField('phone'),
            is_active: true
        };

        const vendorResponse = await api.post(`/api/companies/${newCompany.id}/vendors`, vendorData);

        // Update the invoice
        const updateResponse = await api.put(`/api/invoices/${scanId.value}`, {
            company_id: newCompany.id,
            vendor_id: vendorResponse.data.id
        });

        // Update scan with response data
        scan.value = updateResponse.data;

        // Reload companies list
        await loadCompanies();

        uiStore.showToast({ message: 'Company and vendor created successfully', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to create company', type: 'error' });
        console.error('Failed to create company:', err);
    }
};

const saveBuyerAsCompany = async () => {
    try {
        const companyData = {
            name: getBuyerField('name'),
            vat_number: getBuyerField('vat_number'),
            address: getBuyerField('address'),
            email: getBuyerField('email'),
            phone: getBuyerField('phone'),
            is_active: true
        };

        const newCompany = await createCompany(companyData);

        // Update the invoice with the new company
        const updateResponse = await api.put(`/api/invoices/${scanId.value}`, {
            company_id: newCompany.id
        });

        // Update scan with response data
        scan.value = updateResponse.data;

        uiStore.showToast({ message: 'Company created successfully', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to create company', type: 'error' });
        console.error('Failed to create company:', err);
    }
};

const getSellerField = (field) => {
    if (!scan.value?.result) return '';
    const r = scan.value.result;
    const seller = r.seller || {};

    const fieldMap = {
        name: r.seller_name || seller.name || r.vendor_name || '',
        address: r.seller_address || seller.address || r.vendor_address || '',
        tax_id: r.seller_tax_id || seller.tax_id || '',
        vat_number: ensureBgPrefix(r.seller_vat_number || seller.vat_number || ''),
        phone: r.seller_phone || seller.phone || '',
        email: r.seller_email || seller.email || '',
        authorized_signatory: r.seller_authorized_signatory || seller.authorized_signatory || ''
    };

    return fieldMap[field] || '';
};

const hasSellerField = (field) => {
    const value = getSellerField(field);
    return value && value !== 'N/A' && value.trim() !== '';
};

const getBuyerField = (field) => {
    if (!scan.value?.result) return '';
    const r = scan.value.result;
    const buyer = r.buyer || {};

    const fieldMap = {
        name: r.buyer_name || buyer.name || r.customer_name || '',
        address: r.buyer_address || buyer.address || r.customer_address || '',
        tax_id: r.buyer_tax_id || buyer.tax_id || '',
        vat_number: ensureBgPrefix(r.buyer_vat_number || buyer.vat_number || ''),
        phone: r.buyer_phone || buyer.phone || '',
        email: r.buyer_email || buyer.email || '',
        authorized_signatory: r.buyer_authorized_signatory || buyer.authorized_signatory || ''
    };

    return fieldMap[field] || '';
};

const hasBuyerField = (field) => {
    const value = getBuyerField(field);
    return value && value !== 'N/A' && value.trim() !== '';
};

const getPaymentField = (field) => {
    if (!scan.value?.result) return '';
    const r = scan.value.result;
    const payment = r.payment || {};

    const fieldMap = {
        amount_due: r.payment_amount_due || payment.amount_due || '',
        method: r.payment_method || payment.method || ''
    };

    return fieldMap[field] || '';
};

const getBankField = (field) => {
    if (!scan.value?.result) return '';
    const r = scan.value.result;
    const payment = r.payment || {};
    const bankDetails = payment.bank_details || {};

    const fieldMap = {
        bank_name: r.payment_bank_name || bankDetails.bank_name || '',
        iban: r.payment_iban || bankDetails.iban || '',
        swift_bic: r.payment_swift_bic || bankDetails.swift_bic || '',
        account_number: r.payment_account_number || bankDetails.account_number || ''
    };

    return fieldMap[field] || '';
};

const downloadPdf = async () => {
    try {
        window.location.href = `/api/scans/${scanId.value}/download/pdf`;
        uiStore.showToast({ message: 'Downloading PDF...', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to download PDF', type: 'error' });
    }
};

const downloadOriginal = async () => {
    try {
        // Force download of original file (image or PDF)
        const link = document.createElement('a');
        link.href = `/api/invoices/${scanId.value}/original?download=true`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        uiStore.showToast({ message: 'Downloading original file...', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to download original file', type: 'error' });
    }
};

const viewOriginal = () => {
    window.open(`/api/invoices/${scanId.value}/original`, '_blank');
};

const handleRescan = async (useHardScan) => {
    // Save current form data before rescanning
    const currentFormData = JSON.parse(JSON.stringify(editForm.value));

    const result = await performRescan(scanId.value, useHardScan);
    if (result) {
        // Reload the scan data
        await loadScan();

        // If the invoice is still pending (being reprocessed), restore the form data
        if (scan.value?.status === 'pending' && (!scan.value.result || Object.keys(scan.value.result || {}).length === 0)) {
            editForm.value = currentFormData;
            uiStore.showToast({
                message: t('scanDetails.rescanInProgress'),
                type: 'info',
                duration: 5000
            });
        }
    }
};

const toggleSettingsDropdown = () => {
    // Close export dropdown if open
    if (showExportDropdown.value) {
        closeExportDropdown();
    }
    showSettingsDropdown.value = !showSettingsDropdown.value;
};

const closeSettingsDropdown = () => {
    showSettingsDropdown.value = false;
};

const handleRescanClick = () => {
    closeSettingsDropdown();
    openRescanModal(scanId.value);
};

const handleDelete = async () => {
    closeSettingsDropdown();

    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
        return;
    }

    try {
        await api.delete(`/api/invoices/${scanId.value}`);
        uiStore.showToast({ message: 'Invoice deleted successfully', type: 'success' });
        router.push('/scans');
    } catch (err) {
        uiStore.showToast({ message: 'Failed to delete invoice', type: 'error' });
        console.error('Failed to delete invoice:', err);
    }
};

const handleThumbnailError = (event) => {
    if (event) {
        event.preventDefault?.();
    }
    thumbnailLoaded.value = false;
};

const downloadTxt = async () => {
    try {
        window.location.href = `/api/scans/${scanId.value}/download/txt`;
        uiStore.showToast({ message: 'Downloading TXT...', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to download TXT', type: 'error' });
    }
};

const downloadNapSales = async () => {
    try {
        window.location.href = `/api/scans/${scanId.value}/download/txt?format=nap-sales`;
        uiStore.showToast({ message: 'Downloading NAP Sales format...', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to download NAP Sales format', type: 'error' });
    }
};

const downloadNapPurchases = async () => {
    try {
        window.location.href = `/api/scans/${scanId.value}/download/txt?format=nap-purchases`;
        uiStore.showToast({ message: 'Downloading NAP Purchases format...', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to download NAP Purchases format', type: 'error' });
    }
};

const downloadHtml = async () => {
    try {
        window.location.href = `/api/scans/${scanId.value}/download/html`;
        uiStore.showToast({ message: 'Downloading HTML...', type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Failed to download HTML', type: 'error' });
    }
};

const handleExport = async (format) => {
    try {
        await exportSingle(scanId.value, format);
        uiStore.showToast({ message: `Exporting as ${format.toUpperCase()}...`, type: 'success' });
    } catch (err) {
        uiStore.showToast({ message: 'Export failed', type: 'error' });
    }
};

const addLineItem = () => {
    if (!editForm.value.line_items) {
        editForm.value.line_items = [];
    }
    const newItem = {
        quantity: '',
        description: '',
        unit_price: '',
        total: '',
        tax_rate: '0'
    };
    // Add barcode field for Bulgarian gas station invoices
    if (hasBarcodes.value) {
        newItem.barcode = '';
    }
    editForm.value.line_items.push(newItem);
};

const removeLineItem = (index) => {
    if (confirm('Are you sure you want to delete this line item?')) {
        if (editForm.value.line_items && editForm.value.line_items.length > index) {
            editForm.value.line_items.splice(index, 1);
        }
    }
};

// Calculate final total for a line item after discount
const getLineItemFinalTotal = (item) => {
    let itemTotal = parseFloat(item.total) || 0;

    // Check if there's a numeric discount to apply
    const discountValue = item.discount || '';
    const discountNum = parseFloat(String(discountValue).replace(/[^\d.-]/g, ''));

    if (!isNaN(discountNum) && discountNum !== 0 && !String(discountValue).includes('%')) {
        // If discount is negative (e.g., -1.30), add it (which subtracts)
        // If discount is positive (e.g., 1.30), subtract it
        if (discountNum < 0) {
            itemTotal += discountNum; // Adding negative = subtracting
        } else {
            itemTotal -= discountNum;
        }
    }

    return itemTotal.toFixed(2);
};

const getConfidenceClass = (confidence) => {
    if (confidence >= 80) return 'high';
    if (confidence >= 60) return 'medium';
    return 'low';
};

// Navigation methods
const navigateToPrevious = () => {
    if (previousInvoiceId.value) {
        router.push(`/scans/${previousInvoiceId.value}`);
    }
};

const navigateToNext = () => {
    if (nextInvoiceId.value) {
        router.push(`/scans/${nextInvoiceId.value}`);
    }
};

// Keyboard navigation
const handleKeyboard = (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return; // Don't navigate while typing
    }

    if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateToPrevious();
    } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateToNext();
    }
};

const resetForm = () => {
    initializeEditForm();
    uiStore.showToast({ message: 'Changes reset', type: 'info' });
};

const saveEdit = async () => {
    try {
        // Clone edit form data
        const formData = { ...editForm.value };
        
        // Remove warehouse_number if not using warehouse
        if (!authStore.useWarehouse) {
            delete formData.warehouse_number;
        }
        
        const updateData = {
            result: formData,
            company_id: editForm.value.company_id || null,
            vendor_id: editForm.value.vendor_id || null,
            type: editForm.value.type || null,
            is_checked: editForm.value.is_checked || false
        };

        const response = await api.put(`/api/invoices/${scanId.value}`, updateData);
        scan.value = response.data;
        // Re-initialize to ensure sync
        initializeEditForm();
        
        // Auto-save vendor code to company_vendors if present and user has vendor codes enabled
        if (authStore.useVendorCode && formData.vendor_code && scan.value.company_id) {
            const counterpartyVat = formData.type === 'income' ? formData.buyer_vat_number : formData.seller_vat_number;
            const counterpartyTaxId = formData.type === 'income' ? formData.buyer_tax_id : formData.seller_tax_id;
            
            if (counterpartyVat || counterpartyTaxId) {
                try {
                    // Save vendor code to company_vendors table
                    await api.post('/api/scans/export/save-vendor-codes', {
                        codes: [{
                            vat: counterpartyVat || '',
                            tax_id: counterpartyTaxId || '',
                            code: formData.vendor_code,
                            invoice_ids: [scanId.value]
                        }]
                    });
                    console.log('Auto-saved vendor code to company_vendors:', formData.vendor_code);
                } catch (vendorErr) {
                    console.error('Failed to auto-save vendor code:', vendorErr);
                    // Don't show error to user - this is background operation
                }
            }
        }

        // Don't reset zoom/rotation when saving - preserve user's view state

        $toast.success('Invoice saved successfully!', {
            position: 'top-right',
            duration: 5000
        });
    } catch (err) {
        // Handle 404 - invoice not found or deleted
        if (err.response?.status === 404) {
            $toast.error('Invoice not found. It may have been deleted. Redirecting to invoices list...', {
                position: 'top-right',
                duration: 5000
            });
            setTimeout(() => {
                router.push('/scans');
            }, 2000);
        } else {
            $toast.error('Failed to update invoice', {
                position: 'top-right',
                duration: 5000
            });
        }
        console.error('Failed to save:', err);
    }
};

// Vendor/Company selection modal functions
const openVendorModal = (target) => {
    vendorModalTarget.value = target; // 'seller' or 'buyer'
    showVendorModal.value = true;
};

const closeVendorModal = () => {
    showVendorModal.value = false;
    vendorModalTarget.value = null;
};

const handleVendorSelection = (selection) => {
    const prefix = vendorModalTarget.value; // 'seller' or 'buyer'

    if (selection.type === 'company') {
        // Fill fields from company data
        editForm.value[`${prefix}_name`] = selection.data.name || '';
        editForm.value[`${prefix}_address`] = selection.data.address || '';
        editForm.value[`${prefix}_vat_number`] = selection.data.vat_number || '';
        editForm.value[`${prefix}_tax_id`] = selection.data.tax_id || '';
    } else if (selection.type === 'vendor') {
        // Fill fields from vendor data
        editForm.value[`${prefix}_name`] = selection.data.vendor_name || '';
        editForm.value[`${prefix}_address`] = selection.data.vendor_address || '';
        editForm.value[`${prefix}_vat_number`] = selection.data.vendor_vat || '';
        editForm.value[`${prefix}_tax_id`] = selection.data.vendor_tax_id || '';
    }

    closeVendorModal();
};

// Separate modal functions for seller and buyer
const openSellerModal = () => {
    showSellerModal.value = true;
};

const openBuyerModal = () => {
    showBuyerModal.value = true;
};

const closeSellerModal = () => {
    showSellerModal.value = false;
};

const closeBuyerModal = () => {
    showBuyerModal.value = false;
};

const handleSellerSelection = (selection) => {
    if (selection.type === 'company') {
        editForm.value.seller_name = selection.data.name || '';
        editForm.value.seller_address = selection.data.address || '';
        editForm.value.seller_vat_number = ensureBgPrefix(selection.data.vat_number || '');
        editForm.value.seller_tax_id = selection.data.tax_id || '';
    } else if (selection.type === 'vendor') {
        editForm.value.seller_name = selection.data.vendor_name || '';
        editForm.value.seller_address = selection.data.vendor_address || '';
        editForm.value.seller_vat_number = ensureBgPrefix(selection.data.vendor_vat || '');
        editForm.value.seller_tax_id = selection.data.vendor_tax_id || '';
    }
    closeSellerModal();
};

const handleBuyerSelection = (selection) => {
    if (selection.type === 'company') {
        editForm.value.buyer_name = selection.data.name || '';
        editForm.value.buyer_address = selection.data.address || '';
        editForm.value.buyer_vat_number = ensureBgPrefix(selection.data.vat_number || '');
        editForm.value.buyer_tax_id = selection.data.tax_id || '';
    } else if (selection.type === 'vendor') {
        editForm.value.buyer_name = selection.data.vendor_name || '';
        editForm.value.buyer_address = selection.data.vendor_address || '';
        editForm.value.buyer_vat_number = ensureBgPrefix(selection.data.vendor_vat || '');
        editForm.value.buyer_tax_id = selection.data.vendor_tax_id || '';
    }
    closeBuyerModal();
};

// Functions to save seller/buyer as vendor/company
const openSaveSellerModal = () => {
    // TODO: Implement vendor creation modal with seller data pre-filled
    // For now, show a toast message
    $toast.info('Vendor creation from invoice data will be implemented soon', {
        position: 'top-right',
        duration: 3000
    });
};

const openSaveBuyerModal = () => {
    // TODO: Implement company creation modal with buyer data pre-filled
    // For now, show a toast message
    $toast.info('Company creation from invoice data will be implemented soon', {
        position: 'top-right',
        duration: 3000
    });
};

onMounted(() => {
    loadScan();
    loadCompanies();
    loadDocumentTypes();

    // Close dropdown when clicking outside
    document.addEventListener('click', closeExportDropdown);
    document.addEventListener('click', closeSettingsDropdown);

    // Keyboard navigation
    document.addEventListener('keydown', handleKeyboard);
});

onUnmounted(() => {
    document.removeEventListener('click', closeExportDropdown);
    document.removeEventListener('click', closeSettingsDropdown);
    document.removeEventListener('keydown', handleKeyboard);
});

// Watch for route changes to reload invoice data when navigating
watch(() => route.params.id, (newId, oldId) => {
    if (newId && newId !== oldId) {
        // Reset zoom and rotation when changing invoices
        scale.value = 0.75;
        rotation.value = 0;
        translateX.value = 0;
        translateY.value = 0;

        // Reload the invoice data
        loadScan();
    }
});

/* What each field offers, as a list: the fields are the app's own
   dropdowns now, so the options are data rather than tags. */
const invoicetypeOptions = computed(() => [
  { value: '', label: t('scanDetails.documentTypes.selectPlaceholder') },
  ...(unref(documentTypes) || []).map((docType) => ({ value: docType, label: getDocumentTypeLabel(docType) })),
]);
const paymentmethodOptions = computed(() => [
  { value: '', label: t('scanDetails.selectPaymentMethod') },
  ...(unref(paymentMethods) || []).map((method) => ({ value: method, label: t(`paymentMethods.${method.toLowerCase().replace(/\s+/g, '_')}`, method) })),
]);
</script>

<style scoped>
/* The line-item grid.
 *
 * Each input fills the column it is in rather than carrying a width of its
 * own, so the header widths above are what actually decide the layout. The
 * headers wrap instead of forcing their column wider than its values need —
 * a two-line heading costs nothing, a column sized to its heading costs the
 * description the room it needs. */
.line-items-wrap input,
.line-items-wrap select {
    width: 100%;
    min-width: 0;
}

.line-items-wrap th {
    white-space: normal;
    line-height: 1.2;
    vertical-align: bottom;
}

.line-items-wrap td {
    vertical-align: middle;
}

/* Lifted out of inline style attributes so a theme can reach them — an inline
   !important outranks every stylesheet. The values are unchanged. */
.scan-details-topbar {
    padding: 10px;
    margin: 5px;
}

/* The top bar's controls, in the dashboard's dress.
 *
 * They were the app's generic buttons: 15px type in a 42px box with a solid
 * glyph beside it, which beside the table this page is opened from read as a
 * different product. Hairline edge, 13px label, a drawn mark, and the hover
 * is the edge and the text turning blue — nothing fills. */
.scan-details-topbar .topbar-btn {
    height: 32px;
    padding: 0 12px;
    gap: 7px;
    background: #fff;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    transition: all 0.15s ease;
}

.scan-details-topbar .topbar-btn:hover:not(:disabled) {
    background: #fff;
    border-color: var(--primary, #0697e4);
    color: var(--primary, #0697e4);
}

.scan-details-topbar .topbar-btn:disabled {
    color: var(--text-light, #94a3b8);
    border-color: #e9edf2;
    cursor: default;
}

/* The menu under the settings button: the app's panel, not a shadowed card. */
.topbar-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    min-width: 190px;
    padding: 4px;
    background: #fff;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
    z-index: 9999;
}

.topbar-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    border: none;
    border-radius: 6px;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
}

.topbar-menu-item svg {
    color: var(--primary, #0697e4);
    flex-shrink: 0;
}

.topbar-menu-item:hover {
    background: var(--primary-light, #e0f2fe);
}

.topbar-menu-item--danger,
.topbar-menu-item--danger svg {
    color: #e04545;
}

.topbar-menu-item--danger:hover {
    background: #fff1f1;
}

/* The footer's two actions, at the same scale: export quiet, save the one
   filled button on the page. */
.scan-details-btn {
    height: 32px;
    padding: 0 14px;
    gap: 7px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    transition: all 0.15s ease;
}

.scan-details-btn.btn-secondary {
    background: #fff;
    border: 1px solid #dde3ea;
    color: var(--text-primary, #1e293b);
}

.scan-details-btn.btn-secondary:hover:not(:disabled) {
    background: #fff;
    border-color: var(--primary, #0697e4);
    color: var(--primary, #0697e4);
}

.scan-details-btn.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.scan-details-btn.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
}

.scan-details-header,
.line-items-header,
.line-items-wrap {
    padding: 10px;
}


.scan-details-amount-container {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
}

.scan-details-amount-container > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.scan-details-amount-input,
.scan-details-total-input {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 16px;
    font-weight: 600;
    text-align: right;
    width: 120px;
    background: white;
    transition: all 0.2s ease;
}

@media (max-width: 1023px) {
    .scan-details-amount-input,
    .scan-details-total-input {
        width: 100%;
        font-size: 14px;
        padding: 6px 8px;
        height: 33px;
        box-sizing: border-box;
    }
}

@media (min-width: 1024px) and (max-width: 1300px) {
    .scan-details-amount-input,
    .scan-details-total-input {
        width: 90px;
        font-size: 14px;
    }
}

@media (min-width: 1301px) {
    .scan-details-amount-input,
    .scan-details-total-input {
        width: 180px;
    }
}

.scan-details-amount-input:focus,
.scan-details-total-input:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.scan-details-total-input {
    font-size: 18px;
    font-weight: 700;
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.scan-details-total-input::placeholder {
    background: none;
    -webkit-text-fill-color: #9ca3af;
    color: #9ca3af;
}

.scan-details-currency {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
}

/* Mobile Top Navigation */
@media (max-width: 768px) {
    .flex-shrink-0.h-16 {
        height: auto;
        min-height: 3.5rem;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .flex-shrink-0.h-16 > button,
    .flex-shrink-0.h-16 > div {
        flex-shrink: 0;
    }

    /* Back button takes full width on very small screens */
    @media (max-width: 380px) {
        .flex-shrink-0.h-16 {
            justify-content: center;
        }

        .flex-shrink-0.h-16 > button:first-child {
            order: 1;
        }

        .flex-shrink-0.h-16 > div:nth-child(2) {
            order: 2;
        }

        .flex-shrink-0.h-16 > div:nth-child(3) {
            order: 3;
        }
    }

    .select-seller-btn {
        padding: 4px 8px !important;
        min-width: 32px !important;
        height: 32px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-radius: 4px !important;
        background-color: #4a90e2 !important;
        color: white !important;
        border: none !important;
        cursor: pointer !important;
        transition: background-color 0.2s !important;
    }

    .select-seller-btn svg {
        color: white !important;
    }

    .select-seller-btn:hover {
        background-color: #357abd !important;
    }
}
</style>

